import React, { useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Pencil, X, ChartBar } from "@phosphor-icons/react";

const PRESENCE_COLORS = {
  available: "bg-emerald-500",
  busy: "bg-amber-500",
  away: "bg-slate-400",
  offline: "bg-slate-600",
};

const SPECIALTIES = [
  { value: "ventas", label: "Ventas" },
  { value: "cita_taller", label: "Citas de Taller" },
  { value: "servicio", label: "Servicio" },
  { value: "repuestos", label: "Repuestos" },
  { value: "test_drive", label: "Test Drive" },
  { value: "customer_care", label: "Customer Care" },
  { value: "motocicletas", label: "Motocicletas" },
  { value: "cadillac", label: "Cadillac" },
  { value: "chevrolet", label: "Chevrolet" },
  { value: "gmc", label: "GMC" },
  { value: "buick", label: "Buick" },
  { value: "suzuki", label: "Suzuki" },
  { value: "nissan", label: "Nissan" },
  { value: "infiniti", label: "Infiniti" },
  { value: "yamaha", label: "Yamaha" },
];

export default function Agents({ embedded = false }) {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const [agents, setAgents] = useState([]);
  const [queue, setQueue] = useState([]);
  const [editorAgent, setEditorAgent] = useState(null);

  const canEdit = user && (user.role === "admin" || user.role === "supervisor");

  const load = async () => {
    const { data } = await api.get("/users");
    setAgents(data);
    if (canEdit) {
      try {
        const q = await api.get("/agents/queue-overview");
        setQueue(q.data);
      } catch {
        // supervisor removed or 403 — ignore
      }
    }
  };

  useEffect(() => { load(); }, []);

  const changePresence = async (p) => {
    await api.patch("/users/me/presence", { presence: p });
    setUser((u) => ({ ...u, presence: p }));
    localStorage.setItem("sdm_user", JSON.stringify({ ...user, presence: p }));
    toast.success(`Estado: ${t(`ag.${p}`)}`);
    load();
  };

  return (
    <>
      {!embedded && <Topbar title={t("ag.title")} subtitle={t("ag.subtitle")} />}
      <div className={embedded ? "space-y-6 animate-fade-in" : "p-8 space-y-6 animate-fade-in flex-1 overflow-y-auto min-h-0"}>
        <div className="border border-border rounded-md p-6 bg-card flex items-center justify-between">
          <div>
            <div className="overline mb-1">{t("ag.presence")}</div>
            <div className="text-sm text-muted-foreground">Cambia tu estado</div>
          </div>
          <Select value={user?.presence || "offline"} onValueChange={changePresence}>
            <SelectTrigger className="w-44" data-testid="my-presence">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="available">{t("ag.available")}</SelectItem>
              <SelectItem value="busy">{t("ag.busy")}</SelectItem>
              <SelectItem value="away">{t("ag.away")}</SelectItem>
              <SelectItem value="offline">{t("ag.offline")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {canEdit && queue.length > 0 && (
          <div className="border border-border rounded-md p-5 bg-card" data-testid="queue-overview">
            <div className="flex items-center gap-2 mb-3">
              <ChartBar size={16} className="text-primary" weight="fill" />
              <h3 className="font-semibold">Cola por especialidad</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {queue.filter((q) => q.agents_count > 0 || q.open_conversations > 0).map((q) => (
                <div key={q.specialty} className="border border-border rounded-md p-2.5 bg-background/40" data-testid={`queue-${q.specialty}`}>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{q.specialty}</div>
                  <div className="text-lg font-heading font-bold">{q.open_conversations}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {q.agents_count} agente(s) · {q.agents_available} disp.
                    {q.load_per_agent != null && ` · ${q.load_per_agent}/agent`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="border border-border rounded-md bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                <th className="text-left px-4 py-3">{t("common.name")}</th>
                <th className="text-left px-4 py-3">{t("common.role")}</th>
                <th className="text-left px-4 py-3">{t("common.branch")}</th>
                <th className="text-left px-4 py-3">Especialidades</th>
                <th className="text-left px-4 py-3">{t("ag.presence")}</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.id} className="border-b border-border hover:bg-secondary" data-testid={`agent-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">
                        {a.name[0]}
                      </div>
                      <div>
                        <div className="font-medium">{a.name}</div>
                        <div className="text-xs text-muted-foreground">{a.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><Badge variant="outline" className="uppercase text-[10px]">{a.role}</Badge></td>
                  <td className="px-4 py-3 text-xs">{a.branch || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {(a.specialties || []).slice(0, 4).map((s) => (
                        <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
                      ))}
                      {(a.specialties || []).length > 4 && (
                        <span className="text-[10px] text-muted-foreground">+{(a.specialties || []).length - 4}</span>
                      )}
                      {canEdit && (
                        <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => setEditorAgent(a)} data-testid={`edit-specialties-${a.id}`}>
                          <Pencil size={11} />
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${PRESENCE_COLORS[a.presence] || PRESENCE_COLORS.offline}`}/>
                      <span className="text-xs">{t(`ag.${a.presence || "offline"}`)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <SpecialtiesEditor agent={editorAgent} onClose={() => setEditorAgent(null)} onSaved={load} />
    </>
  );
}

function SpecialtiesEditor({ agent, onClose, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelected((agent?.specialties || []).map((s) => s.toLowerCase()));
  }, [agent?.id]);

  if (!agent) return null;

  const toggle = (v) => {
    setSelected((s) => s.includes(v) ? s.filter((x) => x !== v) : [...s, v]);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${agent.id}/specialties`, { specialties: selected });
      toast.success("Especialidades actualizadas");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="specialties-editor">
        <DialogHeader>
          <DialogTitle>Especialidades · {agent.name}</DialogTitle>
          <DialogDescription>
            Configura las especialidades y marcas para el motor de auto-asignación (round robin inteligente).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="overline mb-2">Especialidad de rol</div>
            <div className="flex flex-wrap gap-1.5">
              {SPECIALTIES.slice(0, 7).map((s) => (
                <button
                  key={s.value}
                  onClick={() => toggle(s.value)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    selected.includes(s.value)
                      ? "border-primary bg-primary/15 text-primary"
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid={`spec-${s.value}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="overline mb-2">Marcas (afinidad)</div>
            <div className="flex flex-wrap gap-1.5">
              {SPECIALTIES.slice(7).map((s) => (
                <button
                  key={s.value}
                  onClick={() => toggle(s.value)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    selected.includes(s.value)
                      ? "border-primary bg-primary/15 text-primary"
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid={`spec-${s.value}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="specialties-save">
            {saving ? "Guardando…" : `Guardar (${selected.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
